/**
 * How a ROUTED HIT lights a light (2026-10-21) - the render spec for the timeline's `instrument` lane, owned here
 * and PORTED by OPBridge, the way `colorSample.ts` + `lightMix.ts` are (OPBridge's `colour_lanes.rs` follows them
 * line for line and a golden file measures the agreement).
 *
 * Why it exists: live-played instruments (OP-MidiFeed hits, routed by a timeline's `hitRoute` steps to a named
 * `instrumentLooks` look) must look exactly like the pre-made timeline (operator: "live kicks would look and act
 * the same as the pre made timeline"), and `/makelights` must preview, flash-check and critique routed hits OFFLINE.
 * So one written rule, here, that both sides render. Agreed with OPBridge S331 on 2026-10-21
 * (`OPApps/opbridge-opbuttons.md`, the instrument-routing ASK); written AGAINST OPBridge's built renderer, not the
 * other way round: `opbridge_lighting::Effect::Hit`, `hit_cover` and `Curve::apply`
 * (`crates/opbridge-lighting/src/lib.rs`, commit 839cec5f) and the compositor's `blend_over`
 * (`crates/opbridge-core/src/lighting.rs`). Any difference from those is flagged below, never silently chosen.
 *
 * THE RULE.
 *   1. A hit draws its look's colour at `level` with a COVER (`hitCover`): rising 0 -> 1 linearly over
 *      `attackBeats`, then `1 - curve(t)` over `fallBeats`, then 0. Beats on the run's musical clock, anchored at
 *      the hit's beat, so the fall follows the live tempo.
 *   2. A cover below 1 blends the hit OVER what the light shows beneath it (its colour lane's rest), per channel,
 *      linearly in PHYSICAL light - OPBridge blends DMX bytes, which are after the level^2.2 gamma - so a hit
 *      settles INTO the rest, never through black (the defect OPBridge measured and fixed: Effect::Flash painted
 *      full cover and fell 140,0,0 -> 0,0,0, then snapped back).
 *   3. Several hits on one light are layered in START order, each blending over the result of the ones before.
 *   4. (2026-10-23, OPBridge S336) How hard a hit is played scales its level (`velocityLevel`), unless its look's
 *      `velocitySensitivity` is 0.
 *
 * CONFIRMED by OPBridge S331 from its source (`617c750a`, 2026-10-21; since `9eb8b330` a hit paints through the
 * COLOUR-LANE path, `Sample::new(rgbw.folded(), 1.0)` + cover + `Some(rgbw)`, and paints nothing at cover 0):
 *   (a) Hits layer in START order: layers sort by priority then `seq`, assigned in start order; a later hit blends
 *       over the earlier hit's paint this frame.
 *   (b) White LEDs: the S329 lane rule - the peak over R, G, B and the whites is the level, each part normalised to
 *       it; every byte, W included, blends like R, G, B. A hit with no `w` pulls a lane's white toward 0.
 *   (c) A colour-WHEEL head: the wheel takes the hit's nearest slot whenever the hit paints (cover > 0) and is never
 *       blended (written before the blend point); the dimmer is. The lane's slot returns when the hit ends
 *       (`hitColourAt(..., { wheel: true })`).
 *   (d) Nothing beneath: OPBridge blends over the buffer (the operator's saved colour, often black), else 0; a
 *       preview has no buffer, so `under = null` blends over black here. A known, accepted preview difference.
 *   (e) Rounding: OPBridge computes in f64 then rounds half away from zero (197.5 -> 198); this spec stays in
 *       floats. Compare goldens within one byte, and keep cases off half bytes.
 */
import type { Rgb01 } from './colorSample.ts';

/** OPBridge's `opbridge_lighting::Curve`, `apply` exactly. Note `smooth` is smoothstep `t*t*(3-2t)`, not the
 *  quadratic ease-in-out the colour lanes use. */
export type HitCurve = 'linear' | 'smooth' | 'easeOut' | 'easeIn';
const finite01 = (t: number) => (Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0);
export function curveApply(curve: HitCurve, t: number): number {
  const x = finite01(t);
  switch (curve) {
    case 'linear': return x;
    case 'smooth': return x * x * (3 - 2 * x);
    case 'easeOut': return 1 - (1 - x) * (1 - x);
    case 'easeIn': return x * x;
  }
}

/** OPBridge's `hit_cover`, exactly: how much of the light a hit holds `beat` beats after it starts. */
export function hitCover(beat: number, attackBeats: number, fallBeats: number, curve: HitCurve = 'easeOut'): number {
  if (!Number.isFinite(beat) || beat < 0) return 0;
  const attack = Number.isFinite(attackBeats) ? Math.max(0, attackBeats) : 0;
  if (beat < attack) return beat / attack;
  if (!(Number.isFinite(fallBeats) && fallBeats > 0)) return 0;
  const t = (beat - attack) / fallBeats;
  return t >= 1 ? 0 : 1 - curveApply(curve, t);
}

/** OPBridge's perceptual-to-physical exponent (`opbridge-lighting::GAMMA`). */
export const HIT_GAMMA = 2.2;

/** A perceptual colour (the timeline's: peak channel = the perceptual level) as the PHYSICAL drive of each channel,
 *  0..1 - what OPBridge writes as bytes / 255: each channel's share of the peak, times the peak^2.2. */
export function toPhysical(c: Rgb01 | null): Rgb01 {
  if (!c) return { r: 0, g: 0, b: 0, w: 0 };
  const ch = [c.r, c.g, c.b, c.w ?? 0].map(finite01);
  const peak = Math.max(...ch);
  if (peak <= 1e-9) return { r: 0, g: 0, b: 0, w: 0 };
  const k = Math.pow(peak, HIT_GAMMA) / peak;
  return { r: ch[0]! * k, g: ch[1]! * k, b: ch[2]! * k, w: ch[3]! * k };
}
/** The inverse of `toPhysical`: physical drives back to the perceptual colour that plays them. `wTemp` is carried. */
export function fromPhysical(p: Rgb01, wTemp?: number): Rgb01 {
  const ch = [p.r, p.g, p.b, p.w ?? 0].map(finite01);
  const peak = Math.max(...ch);
  const out: Rgb01 = { r: 0, g: 0, b: 0 };
  if (peak > 1e-12) {
    const k = Math.pow(peak, 1 / HIT_GAMMA) / peak;
    out.r = ch[0]! * k; out.g = ch[1]! * k; out.b = ch[2]! * k;
    if (ch[3]! > 0) out.w = ch[3]! * k;
  }
  if (wTemp !== undefined) out.wTemp = wTemp;
  return out;
}

/** `blend_over`, per channel in physical light: `cover` of the way from what is under to the hit. */
export function blendOver(under: Rgb01 | null, hit: Rgb01, cover: number): Rgb01 {
  const k = Number.isFinite(cover) ? Math.min(1, Math.max(0, cover)) : 1;
  const u = toPhysical(under); const h = toPhysical(hit);
  const mixed = { r: u.r + (h.r - u.r) * k, g: u.g + (h.g - u.g) * k, b: u.b + (h.b - u.b) * k, w: (u.w ?? 0) + ((h.w ?? 0) - (u.w ?? 0)) * k };
  return fromPhysical(mixed, k >= 0.5 ? hit.wTemp ?? under?.wTemp : under?.wTemp ?? hit.wTemp);
}

/** VELOCITY sets brightness (OPBridge S336, 2026-10-23, `live_lights::velocity_level`, their operator: "yes and on by
 *  default"): a hit of velocity `v` (1-127) on a look with `level` and `velocitySensitivity` `s` (0-1, absent = 1)
 *  draws at `level x (1 - s + s x v / 127)`, perceptual, before the gamma. `s = 0` is every hit at the full level.
 *  A preview from a beat map's analysed onsets (`{ t, v }`, `v` a 0..1 strength) passes `v x 127`. */
export function velocityLevel(level: number, sensitivity: number | undefined, velocity: number): number {
  const s = sensitivity !== undefined && Number.isFinite(sensitivity) ? Math.min(1, Math.max(0, sensitivity)) : 1;
  const v = Math.min(127, Math.max(0, Number.isFinite(velocity) ? velocity : 127)) / 127;
  return level * (1 - s + s * v);
}

/** A look as a routed hit draws it (the render part of an `instrumentLooks` entry). */
export interface HitLook { color: Rgb01; level?: number; attackBeats?: number; fallBeats: number; curve?: HitCurve; velocitySensitivity?: number }
/** One hit: its start on the run's clock (beats), the look it draws, and how hard it was played (1-127; absent = 127,
 *  a full hit, so a case written before velocity counted reads as it did). */
export interface RoutedHit { atBeat: number; look: HitLook; velocity?: number }

/** The colour a light shows at `beat`: what is under it (its lane's colour, or null), with every hit layered over it
 *  in start order (flag (a)). A hit not yet started, or past its fall, leaves the light as it is. */
export function hitColourAt(under: Rgb01 | null, hits: readonly RoutedHit[], beat: number, o: { wheel?: boolean } = {}): Rgb01 | null {
  let out: Rgb01 | null = under;
  /** On a wheel head (flag (c)): the colour of the latest painting hit wins outright; only the level blends. */
  let slotFrom: Rgb01 | null = null;
  const live = [...hits].filter((h) => Number.isFinite(h.atBeat) && h.atBeat <= beat).sort((a, b) => a.atBeat - b.atBeat);
  for (const h of live) {
    const cover = hitCover(beat - h.atBeat, h.look.attackBeats ?? 0, h.look.fallBeats, h.look.curve ?? 'easeOut');
    if (cover <= 0) continue;
    const lv = finite01(velocityLevel(h.look.level ?? 1, h.look.velocitySensitivity, h.velocity ?? 127)); const c = h.look.color;
    // DEC-328: `Sample::new(color, level)` normalises the colour and multiplies its peak INTO the level, so a dark
    // colour is a dark flash, the same as that colour on a lane (operator 2026-10-30; OPBridge's golden, 2026-10-26).
    const top: Rgb01 = { r: c.r * lv, g: c.g * lv, b: c.b * lv, ...(c.w !== undefined ? { w: c.w * lv } : {}), ...(c.wTemp !== undefined ? { wTemp: c.wTemp } : {}) };
    out = blendOver(out, top, cover);
    slotFrom = top;
  }
  if (o.wheel && slotFrom && out) {
    // The wheel shows the hit's slot (its hue, unblended); the blended result's level drives the dimmer.
    const lvl = Math.max(out.r, out.g, out.b, out.w ?? 0);
    const sp = Math.max(slotFrom.r, slotFrom.g, slotFrom.b, slotFrom.w ?? 0);
    if (sp > 1e-9) out = { r: (slotFrom.r / sp) * lvl, g: (slotFrom.g / sp) * lvl, b: (slotFrom.b / sp) * lvl, ...(slotFrom.w !== undefined ? { w: (slotFrom.w / sp) * lvl } : {}) };
  }
  return out;
}
